import { useFundMetrics } from "../lib/hooks";

function fmt$(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

interface MetricsBarProps {
  showUserOnly?: boolean;
}

export default function MetricsBar({ showUserOnly = true }: MetricsBarProps) {
  const { data } = useFundMetrics(showUserOnly);

  // Initialize array with static baseline metrics common to both views
  const globalItems = data ? [
    { label: "Portfolio Value", value: fmt$(data.total_portfolio_value), accent: true },
    { label: "Invested Amount", value: fmt$(data.total_cost) },
    { label: "Total Gain", value: `${data.total_gain_loss >= 0 ? "+" : ""}${fmt$(data.total_gain_loss)}`, gain: data.total_gain_loss },
    { label: "Overall Return", value: `${data.overall_gain_pct >= 0 ? "+" : ""}${data.overall_gain_pct.toFixed(2)}%`, gain: data.overall_gain_pct },
    { label: "Units", value: fmt$(data.total_units) },
    { label: "Avg Price", value: fmt$(data.avg_unit_price) },
    { label: "UnInvested Amount", value: fmt$(data.uninvested_amount) },
  ] : [];

  // Conditional insertion based on view state context
  if (data) {
    if (showUserOnly) {
      // Only append Maturity Date when reviewing individual profile view
      globalItems.push({ label: "Maturity Date", value: String(data.maturity_date) });
    } else {
      // Only append Investor counts when reviewing global overall fund performance
      globalItems.push({ label: "Investors", value: String(data.investor_count) });
    }
  }

  return (
    <div style={S.container}>
      <style>{`
        @media (max-width: 768px) {
          .metrics-grid { gap: 20px 24px !important; }
          .metrics-item { min-width: 120px !important; }
          .metrics-section { padding: 20px 16px !important; }
        }
        @media (max-width: 480px) {
          .metrics-grid { gap: 16px 16px !important; }
          .metrics-item { min-width: calc(50% - 8px) !important; flex: 1 1 calc(50% - 8px) !important; }
        }
      `}</style>
      {globalItems.length > 0 && (
        <div style={S.section} className="metrics-section">
          <div style={S.sectionHeader}>
            <div style={S.sectionTitle}>
              {showUserOnly ? "YOUR PORTFOLIO PERFORMANCE" : "GLOBAL FUND PERFORMANCE"}
            </div>
          </div>
          <div style={S.grid} className="metrics-grid">
            {globalItems.map((it, i) => (
              <div key={i} style={S.item} className="metrics-item">
                <div style={{
                  ...S.value,
                  ...(it.accent ? { color: "var(--teal-light, #1d9e75)" } : {}),
                  ...("gain" in it ? { color: it.gain! >= 0 ? "var(--teal-light, #1d9e75)" : "#e05a5a" } : {}),
                }}>
                  {it.value}
                </div>
                <div style={S.label}>{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 32,
    width: "100%",
    maxWidth: 1100,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  section: {
    background: "#0d1112",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    padding: "24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    boxSizing: "border-box",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255, 255, 255, 0.03)",
    paddingBottom: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.15em",
    color: "#889296",
    textTransform: "uppercase",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "32px 48px",
  },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 140,
  },
  value: {
    fontSize: "clamp(18px, 2.5vw, 26px)",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.01em",
  },
  label: {
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: "0.05em",
    color: "var(--text-muted, #64748b)",
    textTransform: "capitalize",
  },
};